import React, { useState, useRef, useEffect } from 'react';
import { portfolioData } from '../data/mock';
import { User, Code2, Database, Brain, Target, Coffee, Zap, GitBranch, GraduationCap, Award, Languages, X, ShieldCheck, CheckCircle2, MapPin, Phone, Mail, ExternalLink, BookOpen, Eye } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import DecryptedText from './reactbits/DecryptedText';
import SpotlightCard from './reactbits/SpotlightCard';
import BlurText from './reactbits/BlurText';
import ShinyText from './reactbits/ShinyText';

const About = () => {
  const { trackEvent } = useAnalytics();
  const [visibleStats, setVisibleStats] = useState(new Set());
  const [visibleAdditionalStats, setVisibleAdditionalStats] = useState(new Set());
  const [scanlineActive, setScanlineActive] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [certFilter, setCertFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('about');
  const aboutRef = useRef(null);
  const { about } = portfolioData;

  // Intersection Observer for stat animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const statId = entry.target.dataset.statId;
            if (statId) {
              setTimeout(() => {
                if (statId.startsWith('additional-')) {
                  setVisibleAdditionalStats(prev => new Set([...prev, statId]));
                } else {
                  setVisibleStats(prev => new Set([...prev, statId]));
                }
              }, parseInt(entry.target.dataset.delay) || 0);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    const statElements = aboutRef.current?.querySelectorAll('.stat-card, .additional-stat-card');
    statElements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Periodic scanning effect
  useEffect(() => {
    const interval = setInterval(() => {
      setScanlineActive(true);
      setTimeout(() => setScanlineActive(false), 3000);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const getStatIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes('certificate')) return <Award className="stat-icon" />;
    if (l.includes('project')) return <Code2 className="stat-icon" />;
    if (l.includes('technolog')) return <Database className="stat-icon" />;
    if (l.includes('language')) return <Languages className="stat-icon" />;
    return <Zap className="stat-icon" />;
  };

  const handleStatClick = (statLabel) => {
    if (statLabel.toLowerCase().includes('certificate')) {
      trackEvent('button_click', { label: 'About: Certificates Vault' });
      setCertificateModalOpen(true);
    } else if (statLabel.toLowerCase().includes('project')) {
      trackEvent('button_click', { label: 'About: Scroll Projects' });
      document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
    } else if (statLabel.toLowerCase().includes('technolog')) {
      trackEvent('button_click', { label: 'About: Scroll Skills' });
      document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' });
    } else if (statLabel.toLowerCase().includes('language')) {
      trackEvent('button_click', { label: 'About: Languages Spoken' });
      alert("Languages Spoken:\n• Tamil: Native\n• English: Professional");
    } else if (statLabel.toLowerCase().includes('experience') || statLabel.toLowerCase().includes('freelance')) {
      trackEvent('button_click', { label: 'About: Scroll Timeline' });
      document.getElementById('versions')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const filteredCertificates = about.certificates?.filter((cert) => {
    if (certFilter === 'internships') {
      return cert.title.toLowerCase().includes('internship') || cert.issuer.toLowerCase().includes('internship');
    }
    if (certFilter === 'certifications') {
      return cert.issuer.toLowerCase().includes('nptel') || cert.issuer.toLowerCase().includes('cisco') || cert.issuer.toLowerCase().includes('coursera') || cert.issuer.toLowerCase().includes('iit');
    }
    if (certFilter === 'hackathons') {
      return cert.title.toLowerCase().includes('hackathon') || cert.title.toLowerCase().includes('webinar') || cert.issuer.toLowerCase().includes('mitraroop');
    }
    return true;
  }) || [];

  return (
    <section ref={aboutRef} className="about-section" id="about">
      {/* Dynamic Background Elements */}
      <div className="about-bg-grid"></div>
      <div className="digital-particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              '--delay': `${i * 200}ms`,
              '--x': `${Math.random() * 100}%`,
              '--y': `${Math.random() * 100}%`
            }}
          ></div>
        ))}
      </div>

      {/* Scanning Line Effect */}
      {scanlineActive && <div className="about-scanline"></div>}

      <div className="about-container">
        {/* Holographic Header */}
        <div className="section-header">
          <div className="header-terminal">
            <User className="terminal-icon" />
            <span className="terminal-text">
              <DecryptedText text="user.profile.load()" speed={35} maxIterations={8} animateOn="view" />
            </span>
          </div>
          <h2 className="section-title">
            <span className="title-accent">{'{'}</span>
            <DecryptedText text="About Protocol" speed={40} maxIterations={12} animateOn="view" />
            <span className="title-accent">{'}'}</span>
          </h2>
          <div className="title-underline">
            <div className="underline-segment"></div>
            <div className="underline-dot"></div>
            <div className="underline-segment"></div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="about-grid">
          {/* Bio Section with Cyber Spotlight Container */}
          <SpotlightCard className="bio-section-spotlight" spotlightColor="rgba(0, 255, 209, 0.12)">
            <div className="bio-section">
              <div className="bio-header">
                <div className="profile-indicator">
                  <div className="indicator-ring">
                    <div className="ring-pulse"></div>
                  </div>
                  <Brain className="profile-icon" />
                </div>
                <div className="bio-meta">
                  <span className="bio-label">SYSTEM_NEURAL_PROFILE</span>
                  <span className="bio-status">⚡ ONLINE // 99.9% ACCURACY</span>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="system-analysis-tabs">
                <div className="tab-buttons">
                  <button
                    className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
                    onClick={() => setActiveTab('about')}
                  >
                    <User className="tab-icon" />
                    <span>SYSTEM_BIO</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === 'education' ? 'active' : ''}`}
                    onClick={() => setActiveTab('education')}
                  >
                    <GraduationCap className="tab-icon" />
                    <span>ACADEMIC_NODE</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                    onClick={() => setActiveTab('personal')}
                  >
                    <Brain className="tab-icon" />
                    <span>IDENTITY_MATRIX</span>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'about' && (
                  <div className="bio-wrapper-container">
                    <div className="bio-tab-layout">
                    {/* Left Column - 72% Bio Content & Capabilities */}
                    <div className="bio-left-content">
                      <div className="bio-content-header">
                        <h3 className="bio-title">Who Am I</h3>
                        <span className="bio-version-tag">VER_3.0_RELEASE</span>
                      </div>

                      <div className="cyber-bio-wrapper">
                        {/* Paragraph 1 */}
                        <div className="cyber-bio-card">
                          <div className="card-tag">01 // CORE ARCHITECTURE</div>
                          <p className="bio-paragraph">
                            I'm <span className="bio-highlight">Arunkumar</span>, an <span className="bio-highlight-cyan">AI-Augmented Full Stack Developer</span> passionate about building scalable, intelligent, and user-centric web applications. My expertise lies in <span className="tech-pill">React</span>, <span className="tech-pill">Next.js</span>, <span className="tech-pill">TypeScript</span>, <span className="tech-pill">Node.js</span>, <span className="tech-pill">FastAPI</span>, <span className="tech-pill">PostgreSQL</span>, and modern UI/UX, with a strong focus on creating clean, performant, and production-ready solutions.
                          </p>
                        </div>

                        {/* Paragraph 2 */}
                        <div className="cyber-bio-card">
                          <div className="card-tag">02 // ENTERPRISE WORKFLOWS</div>
                          <p className="bio-paragraph">
                            Currently, I'm working on enterprise-grade applications, contributing to features such as <span className="bio-highlight-cyan">role-based access control (RBAC)</span>, dynamic workflows, AI-assisted systems, and interactive document experiences. I enjoy solving complex engineering challenges while designing intuitive user experiences that balance functionality and aesthetics.
                          </p>
                        </div>

                        {/* Paragraph 3 */}
                        <div className="cyber-bio-card">
                          <div className="card-tag">03 // AI SYSTEMS & DESIGN</div>
                          <p className="bio-paragraph">
                            Beyond development, I have experience integrating AI capabilities using <span className="tech-pill">LLM APIs</span>, designing interfaces in <span className="tech-pill">Figma</span>, and building full-stack applications from concept to deployment. I'm always exploring emerging technologies, modern architectures, and AI-powered workflows to create software that delivers real-world impact.
                          </p>
                        </div>

                        {/* Paragraph 4 */}
                        <div className="cyber-bio-card">
                          <div className="card-tag">04 // MISSION & PHILOSOPHY</div>
                          <p className="bio-paragraph">
                            Driven by curiosity and continuous learning, I strive to build products that are not only technically robust but also meaningful, accessible, and enjoyable to use.
                          </p>
                        </div>
                      </div>

                      {/* System Capabilities Matrix */}
                      <div className="capabilities-header">
                        <Zap className="cap-header-icon" />
                        <span>SYSTEM_CAPABILITIES_MATRIX</span>
                      </div>
                      <div className="capabilities-grid">
                        <div className="capability-item">
                          <div className="capability-icon">
                            <Code2 />
                          </div>
                          <div className="cap-info">
                            <span className="cap-name">Full Stack Development</span>
                            <span className="cap-tech">React • Next.js • Node.js</span>
                          </div>
                        </div>
                        <div className="capability-item">
                          <div className="capability-icon">
                            <Database />
                          </div>
                          <div className="cap-info">
                            <span className="cap-name">Backend Architecture</span>
                            <span className="cap-tech">FastAPI • PostgreSQL • SQL</span>
                          </div>
                        </div>
                        <div className="capability-item">
                          <div className="capability-icon">
                            <Brain />
                          </div>
                          <div className="cap-info">
                            <span className="cap-name">AI Systems & Workflows</span>
                            <span className="cap-tech">LLM APIs • Prompt Engineering</span>
                          </div>
                        </div>
                        <div className="capability-item">
                          <div className="capability-icon">
                            <Zap />
                          </div>
                          <div className="cap-info">
                            <span className="cap-name">Enterprise Systems & UI</span>
                            <span className="cap-tech">RBAC • Figma • UX Design</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Metrics Cards */}
                    <div className="bio-right-stats">
                      <div className="stats-grid-vertical">
                        {about.stats.map((stat, index) => {
                          const statId = `stat-${index}`;
                          const isVisible = visibleStats.has(statId);
                          const isCert = stat.label.toLowerCase().includes('certificate');
                          const isProj = stat.label.toLowerCase().includes('project');
                          const isTech = stat.label.toLowerCase().includes('technolog');
                          const isLang = stat.label.toLowerCase().includes('language');

                          const actionLabel = isCert
                            ? 'VIEW VAULT ➔'
                            : isProj
                            ? 'EXPLORE ➔'
                            : isTech
                            ? 'VIEW SKILLS ➔'
                            : isLang
                            ? 'SPOKEN 🗣️'
                            : 'TIMELINE ➔';

                          return (
                            <div
                              key={index}
                              className={`stat-card clickable-stat-card ${isVisible ? 'visible' : ''}`}
                              data-stat-id={statId}
                              data-delay={index * 200}
                              onClick={() => handleStatClick(stat.label)}
                              title={`Click to ${actionLabel}`}
                            >
                              <div className="stat-header">
                                {getStatIcon(stat.label)}
                                <div className="stat-action-badge">
                                  <span>{actionLabel}</span>
                                </div>
                              </div>

                              <div className="stat-content">
                                <div className="stat-value">{stat.value}</div>
                                <div className="stat-label">{stat.label}</div>
                              </div>

                              {/* Data Visualization */}
                              <div className="stat-visualization">
                                <div className="viz-bars">
                                  {[...Array(6)].map((_, i) => (
                                    <div
                                      key={i}
                                      className="viz-bar"
                                      style={{
                                        '--height': `${Math.random() * 100}%`,
                                        '--delay': `${i * 50}ms`
                                      }}
                                    ></div>
                                  ))}
                                </div>
                              </div>

                              {/* Holographic Border */}
                              <div className="stat-border">
                                <div className="border-corner top-left"></div>
                                <div className="border-corner top-right"></div>
                                <div className="border-corner bottom-left"></div>
                                <div className="border-corner bottom-right"></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                    {/* System Log Terminal - Spans 100% Full Width */}
                    <div className="system-info-embedded">
                      <div className="info-terminal">
                        <div className="terminal-header">
                          <div className="terminal-controls">
                            <div className="control-dot red"></div>
                            <div className="control-dot yellow"></div>
                            <div className="control-dot green"></div>
                          </div>
                          <span className="terminal-title">system.log</span>
                        </div>
                        <div className="terminal-body">
                          <div className="terminal-line">
                            <span className="prompt">{'>'}</span>
                            <span className="command">Status:</span>
                            <span className="output">System Operational</span>
                          </div>
                          <div className="terminal-line">
                            <span className="prompt">{'>'}</span>
                            <span className="command">Learning:</span>
                            <span className="output">Continuous Integration</span>
                          </div>
                          <div className="terminal-line">
                            <span className="prompt">{'>'}</span>
                            <span className="command">Next Mission:</span>
                            <span className="output">Building the Future</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'education' && (
                  <div className="academic-node-content">
                    <div className="bio-content-header">
                      <h3 className="bio-title">Academic Journey</h3>
                      <span className="bio-version-tag">FINAL_YEAR_UNDERGRADUATE</span>
                    </div>

                    <div className="cyber-academic-card">
                      <div className="academic-grid-layout">
                        {/* Main Degree Info */}
                        <div className="academic-main-info">
                          <div className="degree-header-badge">
                            <GraduationCap className="deg-badge-icon" />
                            <span>UNDERGRADUATE_DEGREE</span>
                          </div>

                          <h4 className="academic-degree-title">
                            B.E - Computer Science and Engineering (CSE)
                          </h4>

                          <div className="academic-meta-cards">
                            <div className="meta-card">
                              <div className="meta-card-label">INSTITUTION_NODE</div>
                              <div className="meta-card-val">University College of Engineering, Kanchipuram</div>
                            </div>

                            <div className="meta-card">
                              <div className="meta-card-label">TIMELINE_PERIOD</div>
                              <div className="meta-card-val">2022 – 2026 (4 Years)</div>
                            </div>
                          </div>
                        </div>

                        {/* Core Academic Modules */}
                        <div className="academic-focus-info">
                          <div className="focus-header">
                            <BookOpen className="focus-icon" />
                            <span>CORE_ACADEMIC_FOCUS</span>
                          </div>

                          <div className="focus-tags-grid">
                            <span className="focus-pill">Data Structures & Algorithms</span>
                            <span className="focus-pill">Database Management (DBMS)</span>
                            <span className="focus-pill">Operating Systems</span>
                            <span className="focus-pill">Object-Oriented Programming</span>
                            <span className="focus-pill">Full Stack Web Architecture</span>
                            <span className="focus-pill">Computer Networks</span>
                            <span className="focus-pill">Machine Learning Foundations</span>
                            <span className="focus-pill">Software Engineering</span>
                          </div>
                        </div>
                      </div>

                      {/* Card Corner Accents */}
                      <div className="corner-accent top-l"></div>
                      <div className="corner-accent top-r"></div>
                      <div className="corner-accent bot-l"></div>
                      <div className="corner-accent bot-r"></div>
                    </div>
                  </div>
                )}

                {activeTab === 'personal' && (
                  <div className="personal-content">
                    <div className="bio-content-header">
                      <h3 className="bio-title">Identity Matrix</h3>
                      <span className="bio-version-tag">VERIFIED_DEVELOPER_ID</span>
                    </div>

                    <div className="identity-matrix-grid">
                      {/* Full Name */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <User className="id-card-icon" />
                          <span className="id-card-label">FULL_NAME</span>
                        </div>
                        <div className="identity-card-value">Arun Kumar L</div>
                      </div>

                      {/* Education */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <GraduationCap className="id-card-icon" />
                          <span className="id-card-label">DEGREE_&_MAJOR</span>
                        </div>
                        <div className="identity-card-value">B.E - Computer Science and Engineering (CSE)</div>
                      </div>

                      {/* Location */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <MapPin className="id-card-icon" />
                          <span className="id-card-label">LOCATION_NODE</span>
                        </div>
                        <div className="identity-card-value">Kanchipuram, Tamil Nadu, India</div>
                      </div>

                      {/* Phone */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <Phone className="id-card-icon" />
                          <span className="id-card-label">CONTACT_PHONE</span>
                        </div>
                        <a href="tel:+918778929845" className="identity-card-value id-link">
                          +91 8778929845
                          <ExternalLink className="link-icon" />
                        </a>
                      </div>

                      {/* Email */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <Mail className="id-card-icon" />
                          <span className="id-card-label">PRIMARY_EMAIL</span>
                        </div>
                        <a href="mailto:arunkumar582004@gmail.com" className="identity-card-value id-link">
                          arunkumar582004@gmail.com
                          <ExternalLink className="link-icon" />
                        </a>
                      </div>

                      {/* GitHub */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <GitBranch className="id-card-icon" />
                          <span className="id-card-label">GITHUB_PROTOCOL</span>
                        </div>
                        <a href="https://github.com/ARUN-L-KUMAR" target="_blank" rel="noopener noreferrer" className="identity-card-value id-link">
                          github.com/ARUN-L-KUMAR
                          <ExternalLink className="link-icon" />
                        </a>
                      </div>

                      {/* LinkedIn */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <Zap className="id-card-icon" />
                          <span className="id-card-label">LINKEDIN_PROTOCOL</span>
                        </div>
                        <a href="https://linkedin.com/in/arun-l-kumar" target="_blank" rel="noopener noreferrer" className="identity-card-value id-link">
                          linkedin.com/in/arun-l-kumar
                          <ExternalLink className="link-icon" />
                        </a>
                      </div>

                       {/* Languages Known */}
                      <div className="identity-card">
                        <div className="identity-card-header">
                          <Zap className="id-card-icon" />
                          <span className="id-card-label">LANGUAGES_KNOWN</span>
                        </div>
                        <div className="identity-card-value">English and Tamil</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SpotlightCard>
        </div>
      </div>

      {/* Cyber Holographic Certificate Vault Modal */}
      {certificateModalOpen && (
        <div className="cert-modal-backdrop" onClick={() => setCertificateModalOpen(false)}>
          <div className="cert-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Modal Terminal Header */}
            <div className="cert-modal-header">
              <div className="header-left">
                <div className="cyber-terminal-badge">
                  <Award className="badge-icon" />
                  <span>CERTIFICATE_VAULT</span>
                </div>
                <div className="record-count-tag">
                  <span className="pulse-dot"></span>
                  <span>12 VERIFIED RECORDS</span>
                </div>
              </div>

              <button
                className="cert-close-btn"
                onClick={() => setCertificateModalOpen(false)}
                title="Close Vault"
              >
                <X className="close-icon" />
              </button>
            </div>

            {/* Modal Filter Tabs */}
            <div className="cert-filter-bar">
              <button
                className={`cert-filter-btn ${certFilter === 'all' ? 'active' : ''}`}
                onClick={() => setCertFilter('all')}
              >
                ALL_RECORDS ({about.certificates?.length || 0})
              </button>
              <button
                className={`cert-filter-btn ${certFilter === 'internships' ? 'active' : ''}`}
                onClick={() => setCertFilter('internships')}
              >
                INTERNSHIPS
              </button>
              <button
                className={`cert-filter-btn ${certFilter === 'certifications' ? 'active' : ''}`}
                onClick={() => setCertFilter('certifications')}
              >
                CERTIFICATIONS
              </button>
              <button
                className={`cert-filter-btn ${certFilter === 'hackathons' ? 'active' : ''}`}
                onClick={() => setCertFilter('hackathons')}
              >
                HACKATHONS
              </button>
            </div>

            {/* Modal 2-Column Grid */}
            <div className="cert-modal-body">
              <div className="cert-grid">
                {filteredCertificates.map((cert) => (
                  <div key={cert.id} className="cyber-cert-card">
                    <div className="cert-card-top">
                      <div className="cert-icon-box">{cert.icon}</div>
                      <div className="cert-header-meta">
                        <h3 className="cert-card-title">{cert.title}</h3>
                        <span className="cert-issuer-badge">{cert.issuer}</span>
                      </div>
                    </div>

                    <div className="cert-card-mid">
                      <span className="cert-duration-pill">⏰ {cert.duration}</span>
                      <p className="cert-description-text">{cert.description}</p>
                    </div>

                    <div className="cert-card-bottom">
                      <div className="cert-skills-list">
                        {cert.skills?.map((skill, index) => (
                          <span key={index} className="tech-pill">{skill}</span>
                        ))}
                      </div>

                      <a
                        href={cert.credentialUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-cert-btn"
                        title={`View ${cert.title} Certificate`}
                        onClick={(e) => {
                          if (!cert.credentialUrl) {
                            e.preventDefault();
                            alert(`Official verification record confirmed for "${cert.title}" (${cert.issuer}). Status: Authenticated.`);
                          }
                        }}
                      >
                        <Eye className="btn-icon" />
                        <span>VIEW</span>
                        <ExternalLink className="btn-arrow" />
                      </a>
                    </div>

                    {/* Corner Cyber Accents */}
                    <div className="corner-accent top-l"></div>
                    <div className="corner-accent top-r"></div>
                    <div className="corner-accent bot-l"></div>
                    <div className="corner-accent bot-r"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;